import { inject, Injectable } from '@angular/core';

import { map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { finShare } from '@fcom/rx/index';
import { AppState, ConfigService, TzDate } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import {
  FinnairItineraryItemType,
  FinnairLocation,
  FinnairOrderPreview,
  FinnairOrderPreviewsResponse,
} from '@fcom/dapi/api/models';
import { profileType } from '@fcom/core/selectors';
import { Trip, TripsResponse } from '@fcom/common';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { fetchOrderPreviews } from '@fcom/dapi/api/order-functions';

import { ProfileType } from '../../core-api/login';
import { LocationsService } from '../../locations';

const UNKNOWN_DURATION = {
  days: 0,
  hours: 0,
  hoursForDays: 0,
  milliseconds: 0,
  minutes: 0,
};
type LocationMap = Record<string, FinnairLocation>;

@Injectable()
export class OrderPreviewsService {
  private dapiService = inject(DapiService);
  private readonly corporateService = inject(CorporateService);
  private readonly locationsService = inject(LocationsService);
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private store$ = inject<Store<AppState>>(Store);

  private readonly corporateUpcomingTripsRequest = () => ({
    from: 0,
    size: 10,
    fromSaved: false,
    filter: {
      myTrips: false,
      dateRange: {
        fromDate: TzDate.now().toISOString(),
        toDate: undefined,
      },
    },
  });

  getUpcomingTrips(): Observable<FinnairOrderPreviewsResponse> {
    const profileType$ = this.store$.pipe(profileType());

    const isFinnairProfile$: Observable<boolean> = profileType$.pipe(map((type) => type === ProfileType.FPLUS));
    return isFinnairProfile$.pipe(
      switchMap((isFinnairProfile) => {
        if (isFinnairProfile) {
          return this.dapiService
            .invoke$(fetchOrderPreviews, this.configService.cfg.orderUrl, {
              locale: this.languageService.localeValue,
            })
            .pipe(
              map((response: FinnairOrderPreviewsResponse) => ({
                ...response,
                orderPreviews: this.getSortedOrderPreviewsByDepartureDate(response.orderPreviews),
              })),
              finShare()
            );
        } else {
          return this.corporateService
            .getTrips(this.corporateUpcomingTripsRequest()) //
            .pipe(
              mergeMap((response: TripsResponse) => {
                return this.getTripLocations(response.trips).pipe(
                  map((tripsLocations) => {
                    const orderPreviews: FinnairOrderPreview[] = response.trips.map(this.tripToOrderPreview);
                    return { locations: tripsLocations, orderPreviews };
                  })
                );
              }),
              finShare()
            );
        }
      })
    );
  }

  private getTripLocations(trips: Trip[]): Observable<LocationMap> {
    const duplicatedLocationCodes = trips
      .flatMap(({ bounds }) => bounds)
      .flatMap(({ flights }) => flights)
      .flatMap((flight) => [flight.departure.locationCode, flight.arrival.locationCode]);
    const locationCodes = [...new Set(duplicatedLocationCodes)];
    if (locationCodes.length === 0) {
      return of({});
    }
    return this.locationsService
      .triggerLocationFetchAll(locationCodes)
      .pipe(map((locations) => locations as unknown as LocationMap));
  }

  private tripToOrderPreview(trip: Trip): FinnairOrderPreview {
    const firstPassenger = trip.passengers[0];
    return {
      id: trip.bookingReference,
      firstName: firstPassenger.firstName,
      lastName: firstPassenger.lastName,
      numberOfTravelers: trip.passengers.length,
      bounds: trip.bounds.map((bound) => ({
        departure: {
          locationCode: bound.departure.locationCode,
          cityName: bound.departure.locationTitle,
          dateTime: bound.departure.dateTime,
        },
        arrival: {
          locationCode: bound.arrival.locationCode,
          cityName: bound.arrival.locationTitle,
          dateTime: bound.arrival.dateTime,
        },
        itinerary: bound.flights.map((tripFlight) => ({
          flightNumber: tripFlight.flightNumber,
          bookingClass: tripFlight.bookingClass,
          id: tripFlight.id,
          departure: {
            locationCode: tripFlight.departure.locationCode,
            dateTime: tripFlight.departure.dateTime,
          },
          arrival: {
            locationCode: tripFlight.arrival.locationCode,
            dateTime: tripFlight.arrival.dateTime,
          },
          type: FinnairItineraryItemType.FLIGHT,
          duration: UNKNOWN_DURATION,
        })),
      })),
    };
  }

  private getSortedOrderPreviewsByDepartureDate(orderPreviews: FinnairOrderPreview[]): FinnairOrderPreview[] {
    return [...(orderPreviews || [])].sort((previousOrder, nextOrder) =>
      this.compareOrderPreviewDates(previousOrder, nextOrder)
    );
  }

  private compareOrderPreviewDates(previousOrder: FinnairOrderPreview, nextOrder: FinnairOrderPreview): number {
    const previousDate = TzDate.of(previousOrder.bounds[0].departure.dateTime).toLocalDate();
    const nextDate = TzDate.of(nextOrder.bounds[0].departure.dateTime).toLocalDate();
    if (previousDate.gt(nextDate)) {
      return 1;
    }
    if (previousDate.lt(nextDate)) {
      return -1;
    }
    return 0;
  }
}
